import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  CheckCircle, Circle, HandWaving, EnvelopeSimple, MagnifyingGlass,
  FileText, PaperPlaneTilt, Trophy, Handshake
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import API_URL from '../config';

const TIMELINE = [
  { icon: Handshake, title: "You're onboarded", copy: "We've got your details and we're building your file." },
  { icon: MagnifyingGlass, title: 'We run your search', copy: 'We search the USPTO, state registers, and common-law sources for conflicts.' },
  { icon: FileText, title: 'You get your search report', copy: 'A plain-English report with our recommendation, usually 1–2 weeks after onboarding.' },
  { icon: CheckCircle, title: 'You approve, we draft', copy: 'You tell us to proceed, and we draft your application.' },
  { icon: PaperPlaneTilt, title: 'We file with the USPTO', copy: 'Then we track every deadline for you.' },
  { icon: Trophy, title: 'Registration', copy: 'The full process typically takes 12+ months.' },
];

export default function WelcomePage() {
  const { user } = useAuth();
  const [firstName, setFirstName] = useState('');
  const currentStep = 0; // Welcome tab shows during the Onboarding dwell — step 1 is current.

  useEffect(() => {
    axios.get(`${API_URL}/api/client/view-state`)
      .then((res) => setFirstName(res.data.first_name || ''))
      .catch(() => setFirstName((user?.name || '').split(' ')[0] || ''));
  }, [user]);

  return (
    <DashboardLayout>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-3xl"
        data-testid="welcome-page"
      >
        {/* Header */}
        <p className="label-uppercase mb-2" style={{ color: '#1d57a5' }}>Getting Started</p>
        <div className="flex items-start gap-3 mb-2">
          <HandWaving weight="duotone" className="w-8 h-8 sm:w-9 sm:h-9 flex-shrink-0 mt-1" style={{ color: '#ffd100' }} />
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ color: '#141b4d', fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Welcome to Indie Law{firstName ? `, ${firstName}` : ''}.
          </h1>
        </div>
        <p className="text-base mb-8" style={{ color: '#52525B' }}>
          Your brand is in good hands. Here's what's happening and what's next.
        </p>

        {/* Two-step card */}
        <div
          className="p-6 mb-10"
          style={{ backgroundColor: '#f2e9db', borderTop: '3px solid #ffd100' }}
          data-testid="welcome-two-step-card"
        >
          <h2 className="text-lg font-semibold mb-2" style={{ color: '#141b4d' }}>One thing to check off</h2>
          <p className="text-sm mb-5 leading-relaxed" style={{ color: '#101820' }}>
            This came through in an email from us — search your inbox for{' '}
            <span className="inline-flex items-center gap-1 font-semibold">
              <EnvelopeSimple weight="bold" className="w-4 h-4" /> Indie Law
            </span>{' '}
            if you can't spot it.
          </p>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold"
                style={{ backgroundColor: '#141b4d', color: '#ffd100' }}>1</span>
              <p className="text-sm leading-relaxed" style={{ color: '#101820' }}>
                <span className="font-semibold">Your engagement letter.</span> Sent for signature by Dropbox Sign.
              </p>
            </div>
          </div>
          <p className="text-sm mt-5 font-medium" style={{ color: '#006747' }}>
            Already signed? Perfect — nothing else is needed from you right now.
          </p>
        </div>

        {/* Timeline */}
        <h2 className="text-lg font-semibold mb-5" style={{ color: '#141b4d' }}>What happens next</h2>
        <div className="relative" data-testid="welcome-timeline">
          {TIMELINE.map((step, i) => {
            const isCurrent = i === currentStep;
            const isDone = i < currentStep;
            const Icon = step.icon;
            return (
              <div key={i} className="flex gap-4 pb-7 relative">
                {i < TIMELINE.length - 1 && (
                  <div className="absolute left-[15px] top-8 bottom-0 w-px" style={{ backgroundColor: '#bdc5db' }} />
                )}
                <div className="relative z-10 w-8 h-8 flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: isCurrent ? '#ffd100' : isDone ? '#006747' : '#f2e9db',
                    border: isCurrent ? 'none' : '1px solid #bdc5db',
                  }}>
                  <Icon weight={isCurrent ? 'fill' : 'duotone'} className="w-4 h-4"
                    style={{ color: isCurrent ? '#141b4d' : isDone ? '#f2e9db' : '#141b4d' }} />
                </div>
                <div className={isCurrent ? '' : 'opacity-90'}>
                  <p className="text-sm font-semibold flex items-center gap-2" style={{ color: '#141b4d' }}>
                    {step.title}
                    {isCurrent && (
                      <span className="text-[10px] font-bold px-2 py-0.5 uppercase tracking-wide"
                        style={{ backgroundColor: '#006747', color: '#f2e9db' }}>You are here</span>
                    )}
                  </p>
                  <p className="text-sm mt-0.5 leading-relaxed" style={{ color: '#52525B' }}>{step.copy}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Reassurance callout */}
        <div className="p-5 mt-4" style={{ backgroundColor: '#f2e9db', borderTop: '3px solid #ffd100' }}>
          <p className="text-base italic leading-relaxed" style={{ color: '#141b4d' }}>
            "You don't have to figure this out alone — and you definitely don't have to decode the USPTO by yourself."
          </p>
        </div>
      </motion.div>
    </DashboardLayout>
  );
}
